import { CheckCircle2, Circle, Copy, Forward, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { translate } from '@/lib/i18n';

// ============================================================================
// chatSelection.tsx — вид режима МНОЖЕСТВЕННОГО ВЫБОРА сообщений (§2.3):
// маркер выбора на бабле и панель действий над лентой. Логика выбора —
// в useMessageSelection/ChatThread (SSoT), здесь только представление.
// ============================================================================

/** Маркер выбора слева от бабла (заменяет место аватара в режиме выбора). */
export function MessageSelectMarker({ selected }: { selected: boolean }) {
  return (
    <span className="h-8 w-6 shrink-0 flex items-center justify-center">
      {selected
        ? <CheckCircle2 className="h-5 w-5 text-primary" aria-label={translate('chat.selected')} />
        : <Circle className="h-4 w-4 text-muted-foreground/70" aria-label={translate('chat.not_selected')} />}
    </span>
  );
}

interface ChatSelectionBarProps {
  /** Сколько сообщений выбрано */
  count: number;
  /** Есть что копировать (у выбранных есть текст) */
  canCopy: boolean;
  /** Показывать «Переслать» (есть другие чаты) */
  showForward: boolean;
  /** Идёт удаление/пересылка — кнопки блокируются */
  busy: boolean;
  onCopy: () => void;
  onForward: () => void;
  onDelete: () => void;
  onCancel: () => void;
}

export function ChatSelectionBar({ count, canCopy, showForward, busy, onCopy, onForward, onDelete, onCancel }: ChatSelectionBarProps) {
  return (
    <div className="flex items-center gap-1 px-2 py-1.5 border-b shrink-0 glass-panel rounded-none border-x-0 border-t-0">
      <button
        type="button"
        className="p-1.5 rounded-full hover:bg-muted/50"
        onClick={onCancel}
        title={translate('chat.cancel_hint')}
        aria-label={translate('chat.exit_select')}
      >
        <X className="h-4 w-4" />
      </button>
      <span className="flex-1 min-w-0 truncate text-[12px] font-medium px-1">
        {count > 0 ? translate('chat.selected_count', { count }) : translate('chat.select_messages')}
      </span>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" disabled={!canCopy || busy} onClick={onCopy} title={translate('chat.copy_hint')}>
        <Copy className="h-3.5 w-3.5 mr-1" /> {translate('chat.copy_action')}
      </Button>
      {showForward && (
        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" disabled={count === 0 || busy} onClick={onForward} title={translate('chat.forward_hint')}>
          <Forward className="h-3.5 w-3.5 mr-1" /> {translate('chat.forward_action')}
        </Button>
      )}
      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive" disabled={count === 0 || busy} onClick={onDelete} title={translate('chat.hide_selected_hint')}>
        <Trash2 className="h-3.5 w-3.5 mr-1" /> {translate('chat.delete_action')}
      </Button>
    </div>
  );
}
